import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheck } from 'react-icons/fa';
import './Pricing.scss';

interface PricingTier {
  id: number;
  name: string;
  originalPrice: string;
  salePrice: string;
  duration: string;
  features: string[];
  popular?: boolean;
}

const Pricing: React.FC = () => {
  const pricingTiers: PricingTier[] = [
    {
      id: 1,
      name: 'Intimate',
      originalPrice: '£899',
      salePrice: '£449',
      duration: 'Ceremony & Reception (6 hours)',
      features: [
        '6 hours of wedding coverage',
        '1 professional videographer',
        'Ceremony & reception filming',
        '4-6 minute cinematic highlight film',
        'Professional color grading',
        'Licensed music soundtrack',
        'Digital delivery via private gallery',
        '2 rounds of revisions'
      ]
    },
    {
      id: 2,
      name: 'Classic',
      originalPrice: '£1,399',
      salePrice: '£699',
      duration: 'Full Wedding Day (10 hours)',
      features: [
        '10 hours of complete wedding coverage',
        '2 professional videographers',
        'Bridal prep to evening reception',
        '6-8 minute cinematic wedding film',
        'Ceremony footage (unedited)',
        'Professional audio recording',
        'Drone footage (where permitted)',
        'USB drive + digital delivery',
        'Unlimited revisions'
      ],
      popular: true
    },
    {
      id: 3,
      name: 'Luxury',
      originalPrice: '£2,199',
      salePrice: '£1,099',
      duration: 'Complete Wedding Experience',
      features: [
        'Full day unlimited coverage',
        '3 professional videographers',
        'Multi-camera ceremony coverage',
        '10-12 minute documentary-style film',
        'Same-day highlight reel (3 minutes)',
        'Full ceremony & speeches footage',
        'Drone cinematography included',
        'Premium packaging & USB',
        'Engagement shoot included',
        'Custom music licensing'
      ]
    }
  ];

  return (
    <section id="pricing" className="pricing section">
      <div className="container">
        <motion.div 
          className="pricing__header"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="pricing__title">Wedding Film Packages</h2>
          <p className="pricing__subtitle">
            Beautiful wedding films crafted with love and attention to detail. All packages include professional editing and digital delivery.
          </p>
        </motion.div>

        <div className="pricing__grid">
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={tier.id}
              className={`pricing__card ${tier.popular ? 'pricing__card--popular' : ''}`}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              {tier.popular && (
                <div className="pricing__badge">Most Popular</div>
              )}
              
              <div className="pricing__header-card">
                <h3 className="pricing__name">{tier.name}</h3>
                <div className="pricing__price">
                  <span className="pricing__original-price">{tier.originalPrice}</span>
                  <span className="pricing__sale-price">{tier.salePrice}</span>
                </div>
                <p className="pricing__duration">{tier.duration}</p>
              </div>
              
              <ul className="pricing__features">
                {tier.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="pricing__feature">
                    {FaCheck({ className: "pricing__check" })}
                    {feature}
                  </li>
                ))}
              </ul>
              
              <Link to="/contact" className="btn-logo-style pricing__cta">
                Get Started
              </Link>
            </motion.div>
          ))}
        </div>
        
        <motion.div 
          className="pricing__note"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <p>
            * Destination weddings and multi-day celebrations available. 
            Contact us for a personalised quote tailored to your special day.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
